import { useEffect } from 'react';

import { useQueryStates } from 'nuqs';
import { useOptimisticSearchParams } from 'nuqs/adapters/react-router/v7';
import { useForm } from 'react-hook-form';

import { parseDateRangeQuery, serializeDateRangeQuery } from '@crm/shared/lib';

import { DEALS_TABLE_ALLOW_SORT_CLEAR } from './consts';
import {
    DEFAULT_LOST_DEALS_DIRECTION,
    DEFAULT_LOST_DEALS_SORT,
    loadLostDealsSearchParams,
    lostDealsSearchParams,
    lostDealsSearchParamUrlKeys,
} from './search-params';

import type { LostDealsSearchFormProps } from './types';
import type { TLostDealsQueryParams, TLostDealsSortField } from '@crm/entities/deal';
import type { DateRange } from 'react-day-picker';

interface SearchFormValues {
    query: string;
}

export const useFilterByQuery = ({ limit }: { limit: number }) => {
    const [, setQueryParams] = useQueryStates(lostDealsSearchParams, {
        history: 'push',
        urlKeys: lostDealsSearchParamUrlKeys,
    });
    const optimisticSearchParams = useOptimisticSearchParams();
    const searchParams = loadLostDealsSearchParams(optimisticSearchParams);
    const apiSort = searchParams.sort ?? DEFAULT_LOST_DEALS_SORT;
    const apiDirection = searchParams.direction ?? DEFAULT_LOST_DEALS_DIRECTION;
    const queryParams = {
        ...searchParams,
        direction: apiDirection,
        limit,
        sort: apiSort,
    };
    const { handleSubmit, register, reset } = useForm<SearchFormValues>({
        defaultValues: { query: queryParams.query ?? '' },
    });

    useEffect(() => {
        reset({ query: queryParams.query ?? '' });
    }, [queryParams.query, reset]);

    const onSubmit = handleSubmit(({ query }) => {
        const normalizedQuery = query.trim();

        void setQueryParams({
            query: normalizedQuery || null,
            page: 1,
        });
    });

    const resetFilters = () => {
        reset({ query: '' });
        void setQueryParams({
            direction: null,
            lostAtFrom: null,
            lostAtTo: null,
            query: null,
            page: 1,
            sort: null,
        });
    };

    const onLostAtDateFilterChange = (date?: DateRange) => {
        const { from, to } = serializeDateRangeQuery(date);

        void setQueryParams({
            lostAtFrom: from,
            lostAtTo: to,
            page: 1,
        });
    };

    const onPageChange = (page: number) => {
        void setQueryParams({ page });
    };

    const onPageNormalize = (page: number) => {
        void setQueryParams({ page }, { history: 'replace' });
    };

    const onSortChange = ({
        direction,
        sort,
    }: {
        direction: TLostDealsQueryParams['direction'] | null;
        sort: TLostDealsSortField | null;
    }) => {
        void setQueryParams({ direction, sort });
    };

    const hasDataFilters = Boolean(
        queryParams.query || queryParams.lostAtFrom || queryParams.lostAtTo
    );

    return {
        dateFilterProps: {
            lostAt: {
                value: parseDateRangeQuery(queryParams.lostAtFrom, queryParams.lostAtTo),
                onApply: onLostAtDateFilterChange,
            },
        },
        hasDataFilters,
        paginationProps: {
            onPageChange,
            onPageNormalize,
        },
        queryParams: queryParams satisfies TLostDealsQueryParams,
        resetFilters,
        searchFormProps: {
            inputProps: register('query'),
            onSubmit,
        } satisfies LostDealsSearchFormProps,
        tableSortProps: {
            allowClear: DEALS_TABLE_ALLOW_SORT_CLEAR,
            direction: DEALS_TABLE_ALLOW_SORT_CLEAR ? searchParams.direction : apiDirection,
            onSortChange,
            sort: DEALS_TABLE_ALLOW_SORT_CLEAR ? searchParams.sort : apiSort,
        },
    };
};
